import React, { useState, useEffect } from 'react';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import Sidebar from '../components/Sidebar';
import { authStorage } from '../utils/authStorage';
import { fetchInvestmentDetails, fetchParticipants } from '../features/api/apiSlice';
import './ntc-branding.css';

const NTCDashboard = () => {
  const dispatch = useAppDispatch();
  
  // Redux selectors for API data
  const { participants: reduxParticipants, loading: apiLoading } = useAppSelector((state) => state.api);
  
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  
  // Investment data state - aggregated from all participants
  const [investmentData, setInvestmentData] = useState({
    amountInvested: 0,
    totalProjects: 0,
    releasedProjects: 0,
    releasedCount: 0,
    unreleasedAmount: 0,
    unreleasedCount: 0,
    overallROI: 0,
    isLoading: true
  });

  const [allInvestments, setAllInvestments] = useState([]);
  const [participants, setParticipants] = useState([]);

  // Restore sidebar state from localStorage on component mount
  useEffect(() => {
    const savedTheme = localStorage.getItem('nexel-classic-dashboard-menu-mini-theme');
    if (savedTheme === 'menu-mini-theme') {
      setSidebarCollapsed(true);
      document.documentElement.classList.add('minimenu');
    } else {
      setSidebarCollapsed(false);
      document.documentElement.classList.remove('minimenu');
    }
  }, []);

  // Fetch all participants
  useEffect(() => {
    dispatch(fetchParticipants());
  }, [dispatch]);

  // Update participants from Redux
  useEffect(() => {
    if (reduxParticipants) {
      setParticipants(reduxParticipants);
    }
  }, [reduxParticipants]);

  // Fetch all investments from all participants
  useEffect(() => {
    const fetchAllInvestments = async () => {
      if (participants.length === 0) return;
      
      setInvestmentData(prev => ({ ...prev, isLoading: true }));
      
      try {
        // Fetch investments for all participants
        const investmentPromises = participants.map(participant => 
          dispatch(fetchInvestmentDetails(participant.id))
        );
        
        const results = await Promise.all(investmentPromises);
        
        // Aggregate all investments
        const aggregatedInvestments = [];
        const investmentMap = new Map(); // To avoid duplicates by project ID
        
        results.forEach((result, index) => {
          if (fetchInvestmentDetails.fulfilled.match(result)) {
            const investments = result.payload || [];
            investments.forEach(inv => {
              // Use a unique key: project_id + participant_id to handle same project for different participants
              const uniqueKey = `${inv.proj_id || inv.id}_${participants[index].id}`;
              if (!investmentMap.has(uniqueKey)) {
                investmentMap.set(uniqueKey, inv);
                aggregatedInvestments.push(inv);
              }
            });
          }
        });
        
        setAllInvestments(aggregatedInvestments);
        processInvestmentData(aggregatedInvestments);
      } catch (error) {
        console.error('Error fetching all investments:', error);
        setInvestmentData(prev => ({ ...prev, isLoading: false }));
      }
    };

    fetchAllInvestments();
  }, [participants, dispatch]);

  // Helper function to process investment data
  const processInvestmentData = (data) => {
    if (!data || data.length === 0) {
      setInvestmentData({
        amountInvested: 0,
        totalProjects: 0,
        releasedProjects: 0,
        releasedCount: 0,
        unreleasedAmount: 0,
        unreleasedCount: 0,
        overallROI: 0,
        isLoading: false
      });
      return;
    }
    
    let amountInvested = 0;
    let releasedProjects = 0;
    let releasedCount = 0;
    let unreleasedAmount = 0;
    let unreleasedCount = 0;
    let overallROI = 0;
    
    data.forEach(inv => {
      amountInvested += Math.round(inv.allocation_amount);
      
      if (inv.status === 'ongoing' || inv.status === 'completed') {
        releasedProjects += Math.round(inv.allocation_amount);
        releasedCount++;
        if (inv.ntc_roi) {
          overallROI += parseFloat(inv.allocation_amount) * (parseFloat(inv.ntc_roi) / 100);
        }
      } else if (inv.status === 'unreleased') {
        unreleasedAmount += Math.round(inv.allocation_amount);
        unreleasedCount++;
      }
    });
    
    setInvestmentData({
      amountInvested,
      totalProjects: data.length,
      releasedProjects,
      releasedCount,
      unreleasedAmount,
      unreleasedCount,
      overallROI: amountInvested > 0 ? (overallROI / amountInvested) * 100 : 0,
      isLoading: false
    });
  };

  const toggleSidebar = () => {
    const newCollapsedState = !sidebarCollapsed;
    setSidebarCollapsed(newCollapsedState);
    
    // Apply minimenu class to document html element
    if (newCollapsedState) {
      document.documentElement.classList.add('minimenu');
      localStorage.setItem('nexel-classic-dashboard-menu-mini-theme', 'menu-mini-theme');
    } else {
      document.documentElement.classList.remove('minimenu');
      localStorage.setItem('nexel-classic-dashboard-menu-mini-theme', 'menu-expend-theme');
    }
  };

  return (
    <div className="nxl-wrapper">
      {/* Navigation Sidebar */}
      <Sidebar 
        sidebarCollapsed={sidebarCollapsed} 
        setSidebarCollapsed={setSidebarCollapsed}
        toggleSidebar={toggleSidebar}
      />

      {/* Main Content */}
      <main className="nxl-container" style={{ paddingTop: '0' }}>
        <div className="nxl-content" style={{ paddingTop: '0' }}>
          {/* Page Header */}
          <div className="page-header">
            <div className="page-header-left d-flex align-items-center">
              {/* Sidebar Toggle Button */}
              <div className="nxl-navigation-toggle me-3">
                <a href="javascript:void(0);" id="menu-mini-button" onClick={toggleSidebar} style={{ display: sidebarCollapsed ? 'none' : 'block' }}>
                  <i className="feather-align-left"></i>
                </a>
                <a href="javascript:void(0);" id="menu-expend-button" onClick={toggleSidebar} style={{ display: sidebarCollapsed ? 'block' : 'none' }}>
                  <i className="feather-arrow-right"></i>
                </a>
              </div>
              <div className="page-header-title">
                <h5 className="m-b-10">NTC Dashboard</h5>
              </div>
              <ul className="breadcrumb">
                <li className="breadcrumb-item"><a href="/investor-dashboard">Home</a></li>
                <li className="breadcrumb-item">NTC Dashboard</li>
              </ul>
            </div>
            <div className="page-header-right ms-auto d-flex align-items-center gap-3">
              {(() => {
                const userData = authStorage.getLoginData() || {};
                return userData && (
                  <div className="d-flex align-items-center gap-2 d-none d-md-flex">
                    <i className="feather-user text-primary"></i>
                    <span className="fw-semibold text-dark" style={{ fontSize: '14px' }}>
                      Welcome, {userData.name || 'User'}
                    </span>
                  </div>
                );
              })()}
            </div>
          </div>

          {/* Main Content */}
          <div className="main-content">
            {/* Dashboard Title */}
            <div className="mb-4">
              <h4 className="fw-bold text-dark mb-2">
                NTC Dashboard
              </h4>
              <p className="text-muted fs-14 mb-0">Track all investment performance</p>
            </div>
            
            <div className="row">
              {/* [Amount Invested] start */}
              <div className="col-xxl-3 col-md-6">
                <div className="card stretch stretch-full">
                  <div className="card-body">
                    <div className="d-flex align-items-start justify-content-between mb-4">
                      <div className="d-flex gap-4 align-items-center">
                        <div className="avatar-text avatar-lg bg-gray-200">
                          <i className="feather-dollar-sign"></i>
                        </div>
                        <div>
                          <div className="fs-4 fw-bold text-dark">
                            ${investmentData.isLoading ? '...' : investmentData.amountInvested.toLocaleString()}
                          </div>
                          <h3 className="fs-13 fw-semibold text-truncate-1-line">Amount Invested</h3>
                        </div>
                      </div>
                      <a href="javascript:void(0);" className="">
                        <i className="feather-more-vertical"></i>
                      </a>
                    </div>
                    <div className="pt-4">
                      <div className="d-flex align-items-center justify-content-between">
                        <a href="javascript:void(0);" className="fs-12 fw-medium text-muted text-truncate-1-line">Total Investment</a>
                        <div className="w-100 text-end">
                          <span className="fs-12 text-dark">{investmentData.totalProjects} Projects</span>
                          <span className="fs-11 text-muted">({investmentData.releasedCount} Released)</span>
                        </div>
                      </div>
                      <div className="progress mt-2 ht-3">
                        <div className="progress-bar bg-primary" role="progressbar" style={{width: `${investmentData.totalProjects > 0 ? (investmentData.releasedCount / investmentData.totalProjects) * 100 : 0}%`}}></div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              {/* [Amount Invested] end */}

              {/* [Released Projects] start */}
              <div className="col-xxl-3 col-md-6">
                <div className="card stretch stretch-full">
                  <div className="card-body">
                    <div className="d-flex align-items-start justify-content-between mb-4">
                      <div className="d-flex gap-4 align-items-center">
                        <div className="avatar-text avatar-lg bg-gray-200">
                          <i className="feather-check-circle"></i>
                        </div>
                        <div>
                          <div className="fs-4 fw-bold text-dark">
                            {investmentData.isLoading ? '...' : `${investmentData.releasedCount}/${investmentData.totalProjects}`}
                          </div>
                          <h3 className="fs-13 fw-semibold text-truncate-1-line">Released Projects</h3>
                        </div>
                      </div>
                      <a href="javascript:void(0);" className="">
                        <i className="feather-more-vertical"></i>
                      </a>
                    </div>
                    <div className="pt-4">
                      <div className="d-flex align-items-center justify-content-between">
                        <a href="javascript:void(0);" className="fs-12 fw-medium text-muted text-truncate-1-line">Released Investment</a>
                        <div className="w-100 text-end">
                          <span className="fs-12 text-dark">${investmentData.isLoading ? '...' : investmentData.releasedProjects.toLocaleString()}</span>
                          <span className="fs-11 text-muted">({investmentData.amountInvested > 0 ? Math.round((investmentData.releasedProjects / investmentData.amountInvested) * 100) : 0}%)</span>
                        </div>
                      </div>
                      <div className="progress mt-2 ht-3">
                        <div className="progress-bar bg-success" role="progressbar" style={{width: `${investmentData.amountInvested > 0 ? (investmentData.releasedProjects / investmentData.amountInvested) * 100 : 0}%`}}></div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              {/* [Released Projects] end */}

              {/* [Unreleased Projects] start */}
              <div className="col-xxl-3 col-md-6">
                <div className="card stretch stretch-full">
                  <div className="card-body">
                    <div className="d-flex align-items-start justify-content-between mb-4">
                      <div className="d-flex gap-4 align-items-center">
                        <div className="avatar-text avatar-lg bg-gray-200">
                          <i className="feather-clock"></i>
                        </div>
                        <div>
                          <div className="fs-4 fw-bold text-dark">
                            {investmentData.isLoading ? '...' : `${investmentData.unreleasedCount}/${investmentData.totalProjects}`}
                          </div>
                          <h3 className="fs-13 fw-semibold text-truncate-1-line">Unreleased Projects</h3>
                        </div>
                      </div>
                      <a href="javascript:void(0);" className="">
                        <i className="feather-more-vertical"></i>
                      </a>
                    </div>
                    <div className="pt-4">
                      <div className="d-flex align-items-center justify-content-between">
                        <a href="javascript:void(0);" className="fs-12 fw-medium text-muted text-truncate-1-line">Unreleased Investment</a>
                        <div className="w-100 text-end">
                          <span className="fs-12 text-dark">${investmentData.isLoading ? '...' : investmentData.unreleasedAmount.toLocaleString()}</span>
                          <span className="fs-11 text-muted">({investmentData.amountInvested > 0 ? Math.round((investmentData.unreleasedAmount / investmentData.amountInvested) * 100) : 0}%)</span>
                        </div>
                      </div>
                      <div className="progress mt-2 ht-3">
                        <div className="progress-bar bg-warning" role="progressbar" style={{width: `${investmentData.amountInvested > 0 ? (investmentData.unreleasedAmount / investmentData.amountInvested) * 100 : 0}%`}}></div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              {/* [Unreleased Projects] end */}

              {/* [ROI Rate] start */}
              <div className="col-xxl-3 col-md-6">
                <div className="card stretch stretch-full">
                  <div className="card-body">
                    <div className="d-flex align-items-start justify-content-between mb-4">
                      <div className="d-flex gap-4 align-items-center">
                        <div className="avatar-text avatar-lg bg-gray-200">
                          <i className="feather-trending-up"></i>
                        </div>
                        <div>
                          <div className="fs-4 fw-bold text-dark">
                            {investmentData.isLoading ? '...' : `${investmentData.overallROI.toFixed(1)}%`}
                          </div>
                          <h3 className="fs-13 fw-semibold text-truncate-1-line">Overall ROI</h3>
                        </div>
                      </div>
                      <a href="javascript:void(0);" className="">
                        <i className="feather-more-vertical"></i>
                      </a>
                    </div>
                    <div className="pt-4">
                      <div className="d-flex align-items-center justify-content-between">
                        <a href="javascript:void(0);" className="fs-12 fw-medium text-muted text-truncate-1-line">Return on Investment</a>
                        <div className="w-100 text-end">
                          <span className="fs-12 text-dark">{investmentData.releasedCount} Projects</span>
                          <span className="fs-11 text-muted">Released</span>
                        </div>
                      </div>
                      <div className="progress mt-2 ht-3">
                        <div className="progress-bar bg-info" role="progressbar" style={{width: `${Math.min(investmentData.overallROI, 100)}%`}}></div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              {/* [ROI Rate] end */}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default NTCDashboard;
